import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { useDeleteConfirmModal } from 'module/ui/components/atoms/modal/use-delete-confirm-modal';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { HeadingCompact01, Long01 } from 'module/ui/components/styles';
import { faEdit, faFolder, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@rocketlane/ui';
import { KnowledgeBaseFolderLite } from 'api/knowledge-base/types';
import { useDeleteKnowledgeBaseFolderMutation } from 'queries/knowledge-base';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { useKnowledgeBaseNavigation } from '../navigation-utils';

const FolderWrapper = styled.div<{ $isLoading: boolean }>`
  display: flex;
  gap: var(--spacing-05);
  padding: var(--spacing-05);
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-small);
  transition: box-shadow 0.3s ease;
  .knowledge-base-folder-more-actions {
    opacity: 0;
  }
  &:hover {
    box-shadow: var(--shadow-08);
    .knowledge-base-folder-more-actions {
      opacity: 1;
    }
  }
  cursor: pointer;
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      cursor: not-allowed;
      pointer-events: none;
    `};
`;
const FolderInformation = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  flex: 1;
  justify-content: center;
`;
const FolderName = styled.div`
  ${HeadingCompact01};
`;
const FolderDescription = styled.div`
  ${Long01};
  color: var(--text-secondary);
  line-clamp: 2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type Props = {
  folder: KnowledgeBaseFolderLite;
};
export const KnowledgeBaseFolder = ({ folder }: Props) => {
  const { navigateToKnowledgeBaseFolder } = useKnowledgeBaseNavigation();
  const { mutateAsync: deleteFolder, isPending: isLoading } =
    useDeleteKnowledgeBaseFolderMutation();
  const confirmDelete = useDeleteConfirmModal({
    title: useTranslation('knowledge_base.folder.delete.confirm.title'),
    content: useTranslation('knowledge_base.folder.delete.confirm.message'),
    onOk: () => {
      deleteFolder({ folderId: folder.folderId }).then().catch();
    },
  });
  const onEdit = useCallback(() => {
    navigateToKnowledgeBaseFolder(folder.folderId);
  }, [navigateToKnowledgeBaseFolder, folder.folderId]);
  const beforeDelete = useCallback(() => {
    confirmDelete();
  }, [confirmDelete]);

  return (
    <FolderWrapper onClick={onEdit} $isLoading={isLoading}>
      <Icon icon={faFolder} size="1.25x" />
      <FolderInformation>
        <FolderName>{folder.folderName}</FolderName>
        <FolderDescription>
          Central repository for standardized Statement of Work templates and
          samples across enterprise engagements. Includes scope guidelines,
          deliverable definitions, and approval workflows.
        </FolderDescription>
      </FolderInformation>
      <div onClick={(e) => e.stopPropagation()}>
        <MoreActionsDropdown
          className="knowledge-base-folder-more-actions"
          menu={{
            onClick: ({ key }) => {
              if (key === 'edit') {
                onEdit();
              } else if (key === 'delete') {
                beforeDelete();
              }
            },
            items: [
              {
                type: 'group',
                label: <FormattedMessage id="more_actions" />,
                children: [
                  {
                    key: 'edit',
                    label: <FormattedMessage id="edit" />,
                    icon: <FontAwesomeIcon icon={faEdit} />,
                  },
                  {
                    key: 'delete',
                    label: <FormattedMessage id="delete" />,
                    icon: <FontAwesomeIcon icon={faTrash} />,
                    danger: true,
                  },
                ],
              },
            ],
          }}
        />
      </div>
    </FolderWrapper>
  );
};
